import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  Calendar,
  ShieldCheck,
  Timer,
  Users,
  Tent,
  Package,
  Compass,
  AlertTriangle,
  Clock,
  Snowflake,
  Sun,
  Leaf,
  Cloud
} from 'lucide-react';

/**
 * Outdoor & Camping Service Cards
 * Based on wireframe specifications with fit small tile sizes
 */

// Season icons mapping
const SEASON_ICONS = {
  'Spring': <Leaf className="h-3 w-3" />,
  'Summer': <Sun className="h-3 w-3" />,
  'Autumn': <Leaf className="h-3 w-3 text-orange-500" />,
  'Winter': <Snowflake className="h-3 w-3" />,
  'All Year': <Calendar className="h-3 w-3" />,
};

// Card 1: Primary Setup Service (Tent Setup)
export const TentSetupCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  seasonal = null,
  showSeasonalBadge = true,
}) => {
  const isAvailable = !seasonal || seasonal.is_available;
  
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-green-200 bg-green-50/20'
      } ${!isAvailable ? 'opacity-60' : ''}`}
      data-testid="tent-setup-card"
    >
      <CardContent className="p-3">
        {/* Sponsored / Primary Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored</span>
          </div>
        )}
        
        {/* Popular Badge */}
        {service?.badge && (
          <Badge className="bg-green-100 text-green-700 text-xs mb-2">
            {service.badge}
          </Badge>
        )}

        {/* Service Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">⛺</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Tent Setup & Pack-down</h3>
            <p className="text-xs text-gray-500 truncate">Family · Large · Group tents</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Correct pitching</span>
          </p>
          <p className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Weather & safety check</span>
          </p>
        </div>

        {/* Seasonal Badge */}
        {showSeasonalBadge && seasonal && (
          <div className="flex items-center gap-1 text-xs mb-2">
            {isAvailable ? (
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                <Sun className="h-3 w-3 mr-1" />
                Available now
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500 border-gray-200">
                <Snowflake className="h-3 w-3 mr-1" />
                Off-season
              </Badge>
            )}
          </div>
        )}

        {/* Rating & Location Row */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 0})</span>
          </div>
          {provider?.city && (
            <span className="text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {provider.travel_radius_km ? `${provider.travel_radius_km}km` : provider.city}
            </span>
          )}
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 45}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-green-600 hover:bg-green-700"
          onClick={() => onBook?.(service, provider)}
          disabled={!isAvailable}
          data-testid="book-tent-setup"
        >
          {isAvailable ? 'Book setup' : 'Notify me'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Card 2: Rental Service Card
export const RentalCard = ({
  service,
  provider,
  onBook,
  showPricing = 'per_day', // 'per_day', 'per_weekend', 'per_week'
}) => {
  const pricingLabels = {
    per_day: '/day',
    per_weekend: '/weekend',
    per_week: '/week',
  };

  return (
    <Card 
      className="overflow-hidden border border-cyan-200 bg-cyan-50/20 hover:shadow-md transition-all"
      data-testid="rental-card"
    >
      <CardContent className="p-3">
        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎒</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Camping Gear Rental</h3>
            <p className="text-xs text-gray-500">Tent · Sleeping bag · Stove</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Cleaned & checked</span>
          </p>
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Weekend / weekly rates</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 0} rentals)</span>
        </div>

        {/* Pricing Options */}
        <div className="flex gap-1 mb-2">
          <Badge variant="outline" className="text-xs bg-white">
            {provider?.currency || '£'}{service?.price || 20}{pricingLabels[showPricing]}
          </Badge>
        </div>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-cyan-600 hover:bg-cyan-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-rental"
        >
          Rent gear
        </Button>
      </CardContent>
    </Card>
  );
};

// Card 3: Guided Experience Card
export const GuidedExperienceCard = ({
  service,
  provider,
  onBook,
  seasonal = null,
}) => {
  const isAvailable = !seasonal || seasonal.is_available;

  return (
    <Card 
      className={`overflow-hidden border border-amber-200 bg-amber-50/20 hover:shadow-md transition-all ${
        !isAvailable ? 'opacity-60' : ''
      }`}
      data-testid="guided-experience-card"
    >
      <CardContent className="p-3">
        {/* Verified Badge */}
        <Badge className="bg-amber-100 text-amber-700 text-xs mb-2">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Verified Guide
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧭</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Guided Camping Trip</h3>
            <p className="text-xs text-gray-500">Beginner · Family-friendly</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Insured guide</span>
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Equipment advice included</span>
          </p>
        </div>

        {/* Group Size */}
        <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
          <Users className="h-3 w-3" />
          <span>2-10 people</span>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 0})</span>
          </div>
          <span className="text-gray-500">{provider?.city || 'Various locations'}</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 75}</span>
          <span className="text-gray-500">/person</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook?.(service, provider)}
          disabled={!isAvailable}
          data-testid="book-guided-trip"
        >
          {isAvailable ? 'Book trip' : 'Join waitlist'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Card 4: Safety Check Card
export const SafetyCheckCard = ({
  service,
  provider,
  onBook,
  trigger = 'inline', // 'inline', 'post_checkout', 'post_delivery'
}) => {
  const triggerMessages = {
    inline: null,
    post_checkout: "Your tent is on the way! Need help setting it up?",
    post_delivery: "Your gear arrived! Get a safety check for peace of mind",
  };

  return (
    <Card 
      className="overflow-hidden border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white hover:shadow-md transition-all"
      data-testid="safety-check-card"
    >
      <CardContent className="p-3">
        {/* Trigger Message */}
        {triggerMessages[trigger] && (
          <p className="text-xs text-emerald-700 bg-emerald-100 rounded px-2 py-1 mb-2">
            {triggerMessages[trigger]}
          </p>
        )}

        {/* Recommended Badge */}
        <Badge className="bg-emerald-100 text-emerald-700 text-xs mb-2">
          Recommended
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🛡️</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Camping Safety Check</h3>
            <p className="text-xs text-gray-500">Stove · Tent · Anchoring</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Ideal for beginners</span>
          </p>
          <p className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Peace of mind</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 0} checks)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 25}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-emerald-600 hover:bg-emerald-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-safety-check"
        >
          Book safety check
        </Button>
      </CardContent>
    </Card>
  );
};

// Bundle Card
export const BundleCard = ({
  bundle,
  providers,
  currency = '£',
  onBook,
}) => {
  return (
    <Card 
      className="overflow-hidden border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-all"
      data-testid={`bundle-card-${bundle.id}`}
    >
      <CardContent className="p-4">
        {/* Discount Badge */}
        <Badge className="bg-purple-600 text-white text-xs mb-2">
          Save {bundle.discount_percent}%
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-2xl">{bundle.icon || '🏕️'}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{bundle.name}</h3>
            <p className="text-xs text-gray-500">{bundle.description}</p>
          </div>
        </div>

        {/* Included Services */}
        <div className="space-y-1 mb-3">
          <p className="text-xs text-gray-600 font-medium">Includes:</p>
          {bundle.included_services?.map((serviceType, idx) => (
            <p key={idx} className="text-xs text-purple-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
              <span>{serviceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </p>
          ))}
        </div>

        {/* Pricing */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-gray-400 line-through text-sm">
            {currency}{bundle.original_price}
          </span>
          <span className="font-bold text-lg text-purple-700">
            {currency}{bundle.final_price}
          </span>
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            Save {currency}{bundle.savings}
          </Badge>
        </div>

        {/* CTA */}
        <Button 
          className="w-full bg-purple-600 hover:bg-purple-700"
          onClick={() => onBook?.(bundle)}
          data-testid={`book-bundle-${bundle.id}`}
        >
          Get bundle
        </Button>
      </CardContent>
    </Card>
  );
};

// Post-Checkout Upsell Card (Compact)
export const PostCheckoutUpsellCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-white"
      data-testid="post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">⛺</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your tent is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Need help setting it up?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            No thanks
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-blue-600 hover:bg-blue-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add service'} · {provider?.currency || '£'}{service?.price || 45}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill for Outdoor & Camping
export const OutdoorMobilePill = ({
  startingPrice = 20,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="outdoor-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all active:scale-95"
      >
        <span>🏕️</span>
        <span className="text-sm font-medium">Camping help nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Seasonal Availability Banner
export const SeasonalBanner = ({
  currentMonth,
  availableServices,
  unavailableServices,
  country,
}) => {
  if (unavailableServices?.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="seasonal-banner">
      <div className="flex items-start gap-2">
        <Calendar className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">
            Seasonal Availability · {currentMonth}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Some services are seasonal. {unavailableServices.length} service(s) currently unavailable in {country}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default {
  TentSetupCard,
  RentalCard,
  GuidedExperienceCard,
  SafetyCheckCard,
  BundleCard,
  PostCheckoutUpsellCard,
  OutdoorMobilePill,
  SeasonalBanner,
};
